import { useCallback, useMemo, useState, useRef, useEffect } from 'react'
import {
  ReactFlow,
  Controls,
  Background,
  BackgroundVariant,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  ConnectionLineType,
  MarkerType,
  type Node,
  type Edge,
  type Connection,
  type NodeTypes,
  type EdgeTypes,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import {
  Bot,
  UserCheck,
  FileText,
  GitBranch,
  Play,
  Clock,
  CircleStop,
  Mail,
  Webhook,
  Search,
  Database,
  Zap,
  RefreshCw,
  MessageSquare,
  Bell,
} from 'lucide-react'
import { cn } from '@/lib/utils'
// Import enhanced shape nodes
import {
  TriggerShapeNode,
  type TriggerShapeNodeData,
  ConditionShapeNode,
  type ConditionShapeNodeData,
  AgentShapeNode,
  type AgentShapeNodeData,
  ActionShapeNode,
  type ActionShapeNodeData,
  EndShapeNode,
  type EndShapeNodeData,
  HumanReviewShapeNode,
  type HumanReviewShapeNodeData,
} from './shapes'
import AddButtonEdge, { type AddButtonEdgeData } from './AddButtonEdge'

// Define node types for React Flow
export type WorkflowNodeType = 'trigger' | 'agent' | 'humanReview' | 'condition' | 'action' | 'end'

export type WorkflowNodeData =
  | AgentShapeNodeData
  | HumanReviewShapeNodeData
  | TriggerShapeNodeData
  | ConditionShapeNodeData
  | ActionShapeNodeData
  | EndShapeNodeData

export type WorkflowNode = Node<WorkflowNodeData, WorkflowNodeType>

interface WorkflowCanvasProps {
  initialNodes?: WorkflowNode[]
  initialEdges?: Edge[]
  onNodesChange?: (nodes: WorkflowNode[]) => void
  onEdgesChange?: (edges: Edge[]) => void
  onNodeSelect?: (nodeId: string | null) => void
  selectedNodeId?: string | null
  readOnly?: boolean
  hideWidgetLibrary?: boolean
  edgeType?: 'step' | 'smoothstep' | 'bezier' | 'addButton'
  showGrid?: boolean
  snapToGrid?: boolean
  gridSize?: number
  showPageBoundaries?: boolean
}

// Custom node types registration - using enhanced shape nodes
const nodeTypes: NodeTypes = {
  agent: AgentShapeNode,
  humanReview: HumanReviewShapeNode,
  trigger: TriggerShapeNode,
  condition: ConditionShapeNode,
  action: ActionShapeNode,
  end: EndShapeNode,
}

// Custom edge types
const edgeTypes: EdgeTypes = {
  addButton: AddButtonEdge,
}

// Widget library item type
interface WidgetItem {
  type: string
  subType?: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  color: string
}

// Widget library items organized by category
const widgetLibrary: Record<string, { label: string; items: WidgetItem[] }> = {
  triggers: {
    label: 'Triggers',
    items: [
      { type: 'trigger', subType: 'manual', label: 'Manual Start', icon: Play, color: 'bg-blue-500' },
      { type: 'trigger', subType: 'schedule', label: 'Schedule', icon: Clock, color: 'bg-blue-500' },
      { type: 'trigger', subType: 'webhook', label: 'Webhook', icon: Webhook, color: 'bg-blue-500' },
      { type: 'trigger', subType: 'form', label: 'Form', icon: FileText, color: 'bg-blue-500' },
    ],
  },
  ai: {
    label: 'AI & Processing',
    items: [
      { type: 'agent', label: 'AI Agent', icon: Bot, color: 'bg-purple-500' },
      { type: 'humanReview', label: 'Human Review', icon: UserCheck, color: 'bg-cyan-500' },
    ],
  },
  logic: {
    label: 'Logic & Flow',
    items: [
      { type: 'condition', label: 'Condition', icon: GitBranch, color: 'bg-amber-500' },
      { type: 'action', subType: 'delay', label: 'Delay', icon: Clock, color: 'bg-orange-500' },
      { type: 'action', subType: 'loop', label: 'Repeat', icon: RefreshCw, color: 'bg-orange-500' },
    ],
  },
  actions: {
    label: 'Actions',
    items: [
      { type: 'action', subType: 'email', label: 'Send Email', icon: Mail, color: 'bg-emerald-500' },
      { type: 'action', subType: 'webhook', label: 'API Call', icon: Zap, color: 'bg-emerald-500' },
      { type: 'action', subType: 'database', label: 'Database', icon: Database, color: 'bg-emerald-500' },
      { type: 'action', subType: 'document', label: 'Document', icon: FileText, color: 'bg-emerald-500' },
      { type: 'action', subType: 'notification', label: 'Notify', icon: Bell, color: 'bg-emerald-500' },
      { type: 'action', subType: 'sms', label: 'Send SMS', icon: MessageSquare, color: 'bg-emerald-500' },
    ],
  },
  terminal: {
    label: 'Terminal',
    items: [
      { type: 'end', subType: 'success', label: 'Success End', icon: CircleStop, color: 'bg-green-500' },
      { type: 'end', subType: 'failure', label: 'Failure End', icon: CircleStop, color: 'bg-red-500' },
      { type: 'end', label: 'End', icon: CircleStop, color: 'bg-gray-500' },
    ],
  },
}

// Default node data by type - returns data for shape nodes
const getDefaultNodeData = (type: WorkflowNodeType, subType?: string): WorkflowNodeData => {
  switch (type) {
    case 'agent':
      return {
        label: 'AI Agent',
        agentName: 'AI Agent',
        status: 'idle',
      } as AgentShapeNodeData
    case 'humanReview':
      return {
        label: 'Human Review',
        reviewTitle: 'Review Required',
        allowedActions: ['approve', 'reject'],
        status: 'idle',
      } as HumanReviewShapeNodeData
    case 'trigger':
      return {
        label:
          subType === 'manual'
            ? 'Manual Start'
            : subType === 'schedule'
              ? 'Schedule'
              : subType === 'webhook'
                ? 'Webhook'
                : subType === 'form'
                  ? 'Form Submission'
                  : subType === 'event'
                    ? 'Event'
                    : 'Trigger',
        triggerType: (subType as 'form' | 'schedule' | 'webhook' | 'manual' | 'event') || 'manual',
        status: 'idle',
      } as TriggerShapeNodeData
    case 'condition':
      return {
        label: 'Condition',
        condition: '',
        status: 'idle',
      } as ConditionShapeNodeData
    case 'action':
      return {
        label:
          subType === 'email'
            ? 'Send Email'
            : subType === 'webhook'
              ? 'API Call'
              : subType === 'document'
                ? 'Generate Document'
                : subType === 'database'
                  ? 'Database'
                  : subType === 'notification'
                    ? 'Send Notification'
                    : subType === 'sms'
                      ? 'Send SMS'
                      : subType === 'delay'
                        ? 'Wait'
                        : subType === 'loop'
                          ? 'Repeat'
                          : 'Action',
        actionType: (subType as 'email' | 'webhook' | 'database' | 'document' | 'notification' | 'sms' | 'delay' | 'loop' | 'custom') || 'email',
        status: 'idle',
      } as ActionShapeNodeData
    case 'end':
      return {
        label: subType === 'success' ? 'Success' : subType === 'failure' ? 'Failure' : 'End',
        endType: (subType as 'success' | 'failure' | 'default' | 'terminate') || 'default',
      } as EndShapeNodeData
    default:
      return { label: 'Node' } as EndShapeNodeData
  }
}

// Global callback store for edge add node functionality
const edgeCallbackStore = new Map<string, (edgeId: string, nodeType: string, subType?: string) => void>()

export function WorkflowCanvas({
  initialNodes = [],
  initialEdges = [],
  onNodesChange: onNodesChangeCallback,
  onEdgesChange: onEdgesChangeCallback,
  onNodeSelect,
  selectedNodeId,
  readOnly = false,
  hideWidgetLibrary = false,
  edgeType = 'step',
  showGrid = true,
  snapToGrid = true,
  gridSize = 20,
  showPageBoundaries = false,
}: WorkflowCanvasProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes as Node[])
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)
  const [searchQuery, setSearchQuery] = useState('')
  const [draggedItem, setDraggedItem] = useState<{ type: string; subType?: string } | null>(null)
  const reactFlowWrapper = useRef<HTMLDivElement>(null)
  const instanceId = useRef(`canvas-${Date.now()}`)

  // Register the add node callback globally
  const addNodeOnEdge = useCallback(
    (edgeId: string, nodeType: string, subType?: string) => {
      if (readOnly) return

      const edge = edges.find((e) => e.id === edgeId)
      if (!edge) return

      const sourceNode = nodes.find((n) => n.id === edge.source)
      const targetNode = nodes.find((n) => n.id === edge.target)
      if (!sourceNode || !targetNode) return

      // Calculate position between source and target
      const newX = (sourceNode.position.x + targetNode.position.x) / 2
      const newY = (sourceNode.position.y + targetNode.position.y) / 2 + 80

      const newNodeId = `node-${Date.now()}`
      const newNode: WorkflowNode = {
        id: newNodeId,
        type: nodeType as WorkflowNodeType,
        position: { x: newX, y: newY },
        data: getDefaultNodeData(nodeType as WorkflowNodeType, subType),
        selected: false,
      }

      // Create new edges
      const pathType = edgeType === 'addButton' ? 'step' : edgeType
      const arrowMarker = { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#94a3b8' }
      const newEdge1: Edge<AddButtonEdgeData> = {
        id: `e${edge.source}-${newNodeId}`,
        source: edge.source,
        target: newNodeId,
        type: 'addButton',
        data: { onAddNode: (eid, nt, st) => edgeCallbackStore.get(instanceId.current)?.(eid, nt, st), pathType },
        sourceHandle: edge.sourceHandle,
        markerEnd: arrowMarker,
      }

      const newEdge2: Edge<AddButtonEdgeData> = {
        id: `e${newNodeId}-${edge.target}`,
        source: newNodeId,
        target: edge.target,
        type: 'addButton',
        data: { onAddNode: (eid, nt, st) => edgeCallbackStore.get(instanceId.current)?.(eid, nt, st), pathType },
        targetHandle: edge.targetHandle,
        markerEnd: arrowMarker,
      }

      // Update nodes
      const newNodes = [...nodes, newNode as Node]
      setNodes(newNodes)

      // Update edges
      const filteredEdges = edges.filter((e) => e.id !== edgeId)
      const newEdges = [...filteredEdges, newEdge1, newEdge2]
      setEdges(newEdges)

      // Notify parent after state updates
      setTimeout(() => {
        onNodesChangeCallback?.(newNodes as WorkflowNode[])
        onEdgesChangeCallback?.(newEdges)
        onNodeSelect?.(newNodeId)
      }, 0)
    },
    [nodes, edges, setNodes, setEdges, onNodesChangeCallback, onEdgesChangeCallback, onNodeSelect, readOnly, edgeType]
  )

  // Register callback in store
  useEffect(() => {
    edgeCallbackStore.set(instanceId.current, addNodeOnEdge)
    return () => {
      edgeCallbackStore.delete(instanceId.current)
    }
  }, [addNodeOnEdge])

  // Create stable callback for edges
  const stableEdgeCallback = useCallback(
    (edgeId: string, nodeType: string, subType?: string) => {
      edgeCallbackStore.get(instanceId.current)?.(edgeId, nodeType, subType)
    },
    []
  )

  // Handle connection between nodes
  const onConnect = useCallback(
    (params: Connection) => {
      if (readOnly) return

      const pathType = edgeType === 'addButton' ? 'step' : edgeType
      const newEdge: Edge<AddButtonEdgeData> = {
        ...params,
        id: `e${params.source}-${params.target}`,
        type: 'addButton',
        data: { onAddNode: stableEdgeCallback, pathType },
        markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#94a3b8' },
      } as Edge<AddButtonEdgeData>

      setEdges((eds) => {
        const newEdges = addEdge(newEdge, eds)
        setTimeout(() => onEdgesChangeCallback?.(newEdges), 0)
        return newEdges
      })
    },
    [setEdges, onEdgesChangeCallback, readOnly, stableEdgeCallback, edgeType]
  )

  // Sync nodes when initialNodes prop changes (e.g., when template is applied)
  useEffect(() => {
    if (initialNodes.length > 0) {
      setNodes(initialNodes as Node[])
    }
  }, [initialNodes, setNodes])

  // Sync edges when initialEdges prop changes (e.g., when template is applied)
  useEffect(() => {
    if (initialEdges.length > 0) {
      const pathType = edgeType === 'addButton' ? 'step' : edgeType
      const arrowMarker = { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#94a3b8' }
      setEdges(
        initialEdges.map((edge) => ({
          ...edge,
          type: 'addButton',
          data: { ...edge.data, onAddNode: stableEdgeCallback, pathType },
          markerEnd: arrowMarker,
        }))
      )
    }
  }, [initialEdges, setEdges, stableEdgeCallback, edgeType])

  // Handle node selection
  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      onNodeSelect?.(node.id)
    },
    [onNodeSelect]
  )

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    onNodeSelect?.(null)
  }, [onNodeSelect])

  // Drag from widget library
  const onDragStart = useCallback(
    (event: React.DragEvent, type: string, subType?: string) => {
      event.dataTransfer.setData('application/reactflow', JSON.stringify({ type, subType }))
      event.dataTransfer.effectAllowed = 'move'
      setDraggedItem({ type, subType })
    },
    []
  )

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      setDraggedItem(null)

      if (readOnly) return

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect()
      if (!reactFlowBounds) return

      const data = event.dataTransfer.getData('application/reactflow')
      if (!data) return

      const { type, subType } = JSON.parse(data)

      // Calculate position relative to canvas
      const position = {
        x: event.clientX - reactFlowBounds.left - 100,
        y: event.clientY - reactFlowBounds.top - 30,
      }

      const newNodeId = `node-${Date.now()}`
      const newNode: WorkflowNode = {
        id: newNodeId,
        type: type as WorkflowNodeType,
        position,
        data: getDefaultNodeData(type as WorkflowNodeType, subType),
        selected: false,
      }

      setNodes((nds) => {
        const newNodes = [...nds, newNode as Node]
        setTimeout(() => onNodesChangeCallback?.(newNodes as WorkflowNode[]), 0)
        return newNodes
      })

      onNodeSelect?.(newNodeId)
    },
    [setNodes, onNodesChangeCallback, onNodeSelect, readOnly]
  )

  const onDragEnd = useCallback(() => {
    setDraggedItem(null)
  }, [])

  // Filter widgets by search
  const filteredWidgets = useMemo(() => {
    if (!searchQuery) return widgetLibrary

    const filtered: Record<string, { label: string; items: WidgetItem[] }> = {}

    Object.entries(widgetLibrary).forEach(([key, category]) => {
      filtered[key] = {
        label: category.label,
        items: category.items.filter((item) =>
          item.label.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      }
    })

    return filtered
  }, [searchQuery])

  // Memoize minimap node colors
  const minimapNodeColor = useCallback((node: Node) => {
    switch (node.type) {
      case 'agent':
        return '#7c3aed'
      case 'humanReview':
        return '#06b6d4'
      case 'trigger':
        return '#3b82f6'
      case 'condition':
        return '#f59e0b'
      case 'action':
        return '#10b981'
      case 'end':
        return '#6b7280'
      default:
        return '#6b7280'
    }
  }, [])

  // Apply selection styling
  const nodesWithSelection = useMemo(() => {
    return nodes.map((node) => ({
      ...node,
      selected: node.id === selectedNodeId,
    }))
  }, [nodes, selectedNodeId])

  return (
    <div className="flex h-full">
      {/* Main Canvas Area */}
      <div
        ref={reactFlowWrapper}
        className="flex-1 relative bg-gray-50 dark:bg-gray-900 overflow-hidden"
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        <ReactFlow
          nodes={nodesWithSelection}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          nodesDraggable={!readOnly}
          nodesConnectable={!readOnly}
          elementsSelectable={!readOnly}
          snapToGrid={snapToGrid}
          snapGrid={[gridSize, gridSize]}
          proOptions={{ hideAttribution: true }}
          defaultEdgeOptions={{
            type: edgeType === 'addButton' ? 'addButton' : edgeType,
            animated: false,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 20,
              height: 20,
              color: '#94a3b8',
            },
          }}
          connectionLineStyle={{ stroke: '#9333ea', strokeWidth: 2 }}
          connectionLineType={
            edgeType === 'step' ? ConnectionLineType.Step :
            edgeType === 'smoothstep' ? ConnectionLineType.SmoothStep :
            ConnectionLineType.Bezier
          }
        >
          {showGrid && (
            <Background
              id="grid-bg"
              variant={BackgroundVariant.Dots}
              gap={gridSize}
              size={1}
              color="#cbd5e1"
              className="dark:opacity-20"
            />
          )}

          {/* Page Boundaries - US Letter size (816x1056 at 96 DPI) */}
          {showPageBoundaries && (
            <Background
              id="page-bg"
              variant={BackgroundVariant.Lines}
              gap={[816, 1056]}
              color="#3b82f6"
              lineWidth={1}
              style={{ opacity: 0.3 }}
            />
          )}

          <Controls
            showInteractive={false}
            className="!bg-white dark:!bg-gray-800 !border-gray-200 dark:!border-gray-700 !rounded-xl !shadow-lg"
          />

          <MiniMap
            nodeColor={minimapNodeColor}
            maskColor="rgba(0, 0, 0, 0.1)"
            className="!bg-white dark:!bg-gray-800 !border-gray-200 dark:!border-gray-700 !rounded-xl !shadow-lg"
            style={{ width: 120, height: 80 }}
          />
        </ReactFlow>

        {/* Drop indicator overlay */}
        {draggedItem && (
          <div className="absolute inset-0 border-2 border-dashed border-purple-400 bg-purple-50/20 dark:bg-purple-900/10 pointer-events-none flex items-center justify-center">
            <div className="px-4 py-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-purple-300 dark:border-purple-600">
              <p className="text-sm font-medium text-purple-600 dark:text-purple-400">
                Drop to add "{draggedItem.type}" node
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Widget Library Sidebar */}
      {!readOnly && !hideWidgetLibrary && (
        <div className="w-64 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Widget Library</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Drag widgets into the canvas
            </p>
          </div>

          {/* Search */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search widgets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-9 pl-9 pr-3 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Widget Categories */}
          <div className="flex-1 overflow-y-auto p-3 space-y-4">
            {Object.entries(filteredWidgets).map(([key, category]) => {
              if (category.items.length === 0) return null

              return (
                <div key={key}>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">
                    {category.label}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {category.items.map((item, idx) => {
                      const Icon = item.icon
                      return (
                        <div
                          key={`${item.type}-${item.subType || idx}`}
                          draggable
                          onDragStart={(e) => onDragStart(e, item.type, item.subType)}
                          onDragEnd={onDragEnd}
                          className={cn(
                            'flex flex-col items-center gap-1.5 p-3 rounded-xl cursor-grab active:cursor-grabbing',
                            'bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600',
                            'hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-gray-200 dark:hover:border-gray-500',
                            'transition-all duration-150 hover:shadow-md'
                          )}
                        >
                          <div className={cn('w-9 h-9 rounded-xl flex items-center justify-center', item.color)}>
                            <Icon className="w-4.5 h-4.5 text-white" />
                          </div>
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-300 text-center leading-tight">
                            {item.label}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Footer hint */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              Click + on edges to insert nodes
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
